<script setup>
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useCarAreasStore } from '@/stores/car-areas'
import UTACarAreasForm from '@/components/car/UTACarAreasForm.vue'
import Modal from '@/components/UTAModal.vue'

const carAreasOnPage = 10

const props = defineProps({
    uuid: {
        type: String,
        required: true
    }
})

// Store
const translaterStore = useTranslaterStore()
const carAreasStore = useCarAreasStore()

// Data
const area = ref('')
const loader = ref(false)
const isShowConfigureModal = ref(false)
const isShowDeleteModal = ref(false)
const currentCarArea = ref(null)
const isShowDeleteMsg = ref(false)
const pageNumber = ref(1)

// Computed
const getTranslate = computed(() => translaterStore.getTranslate)
const carAreas = computed(() => carAreasStore.carAreas)
// const carAreasProcess = computed(() => carAreasStore.carAreasProcess)
const carAreasDeleteMsg = computed(() => carAreasStore.carAreasDeleteMsg)

function getCarEventArea(uuid) {
    const area = carAreas.value?.areas?.find(area => area.uuid == uuid)

    return area?.name || false
}

function openConfigureModal() {
    isShowConfigureModal.value = true

    closeDeleteMsg()
}

function openDeleteModal(id) {
    isShowDeleteModal.value = true

    closeDeleteMsg()

    if(id) {
        currentCarArea.value = carAreas.value?.array?.find(item => item.id == id)
    }
}

function closeConfigureModal() {
    isShowConfigureModal.value = false

    area.value = ''
    currentCarArea.value = null
}

function closeDeleteModal() {
    isShowDeleteModal.value = false

    area.value = ''
    currentCarArea.value = null
}

function closeDeleteMsg() {
    carAreasStore.setCarAreasDeleteMsg({})
    isShowDeleteMsg.value = false
}

async function changePage(cursor, dir) {
    const isUseStore = true
    const params = {
        'uuid_car_array': props.uuid,
        'c.limit': carAreasOnPage,
        'c.direction': dir, 
        'c.cursor': cursor,
    }

    loader.value = true
        
    await carAreasStore.readCarAreas(params, isUseStore)

    if(carAreas.value?.array?.length) {
        dir == 'after' ? 
            pageNumber.value++ : 
            pageNumber.value--
    }

    loader.value = false
}

async function updateCarAreasList() {
    const isUseStore = true
    const params = {
        'uuid_car_array': props.uuid,
        'c.limit': carAreasOnPage
    }

    loader.value = true
        
    await carAreasStore.readCarAreas(params, isUseStore)

    loader.value = false
}

async function removeCarArea () {

    if(currentCarArea.value?.id) {
        await carAreasStore.deleteCarArea(currentCarArea.value.id)        
    }

    if(carAreasDeleteMsg.value?.success == true) {

        isShowDeleteMsg.value = true

        closeDeleteModal()

        await updateCarAreasList()
        
        pageNumber.value = 1
    }
}

onBeforeMount(async () => {
    await updateCarAreasList()
})

onBeforeUnmount(() => {
    closeDeleteMsg()
})
</script>

<template>
    <div class="uta-car-areas">
        <header class="uta-page-header" role="heading" aria-level="1">
            <h1 class="uta-page-header__title">
                {{ getTranslate.AREAS }}
            </h1>
            <div class="uta-page-header__actions">
                <button 
                        class="uta-page-header__action uta-btn uta-btn--main uta-btn--sm uta-btn--has-icon" 
                        type="button" 
                        :title="getTranslate.BTN_CTRL"
                        @click="openConfigureModal"
                    >
                    <span class="uta-btn__content">
                        <span class="uta-btn__icon material-icons"> tune </span>
                        <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ getTranslate.BTN_CTRL }}</span>
                    </span>
                </button>                
            </div>
        </header>

        <div v-if="loader" class="d-flex justify-content-center align-items-center h-100">
            <div class="loader">
                <div class="spinner-border text-secondary" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </div>
        </div>
        
        <div v-else class="container-fluid">
            <div v-if="isShowDeleteMsg" class="row mb-3">
                <div class="uta-alert uta-alert--success">
                    <span class="material-icons uta-alert__icon">
                        error_outline
                    </span>
                    <div class="uta-alert__text">
                        {{ getTranslate[carAreasDeleteMsg.text] || carAreasDeleteMsg.text }}
                    </div>
                    <button type="button" class="uta-alert__close" @click="closeDeleteMsg">
                        <span class="material-icons">close</span>
                    </button>
                </div>
            </div>

            <div v-if="!carAreas?.array?.length" class="row mb-3">
                <div class="uta-alert uta-alert--danger">
                    <span class="material-icons uta-alert__icon">error_outline</span>
                    <div class="uta-alert__text">
                        {{ getTranslate.EEventParamNotFound }}
                    </div>
                </div>
            </div>

            <div v-if="carAreas?.array?.length" class="uta-list uta-car-areas__list">
                <div class="uta-list__title d-none d-md-block uta-car-areas__title">
                    <div class="row">
                        <div class="col-5">{{ getTranslate.CAR_AREAS_AREA}}</div>
                        <div class="col-5">{{ getTranslate.CAR_AREAS_EVENT }}</div>
                    </div>
                </div>

                <div class="uta-list__content">
                    <div 
                        class="row align-items-center uta-list__item uta-car-areas__area" 
                        :class="{ 'uta-list__item--dark' : index % 2 }" 
                        v-for="(item, index) in carAreas.array" 
                        :key="item.id"
                    >
                        <div class="col-12 col-md-5 uta-car-areas__area-name">
                            <router-link 
                                :to="'/areas/' + item.area"
                                class="list__item-link"
                            >
                                {{ getCarEventArea(item.area) }}
                            </router-link>
                        </div>
                        <div class="col-12 col-md-5 uta-car-areas__area-date">
                            {{ getTranslate[`EVENT_${item.type.toUpperCase()}`] || item.type }}
                        </div>
                        <div class="col-12 col-sm-6 col-md-2 uta-car-areas__area-control">
                            <div class="d-flex justify-content-md-end align-items-center">
                                <a 
                                    href="#" 
                                    class="uta-users__user-delete d-none d-md-inline-block" 
                                    @click.prevent="openDeleteModal(item.id)"
                                >
                                    <span class="material-icons"> delete </span>
                                </a>
                                <a 
                                    class="uta-btn uta-btn--main uta-btn--sm uta-btn--has-icon d-md-none mt-3" 
                                    href="#"  
                                    :title="getTranslate.BTN_CTRL"
                                    @click.prevent="openDeleteModal(item.id)"
                                >
                                <span class="uta-btn__content">
                                    <span class="uta-btn__icon material-icons"> delete </span>
                                    <span class="uta-btn__text uta-btn__text--has-icon">{{ getTranslate.BTN_DELETE }}</span>
                                </span>
                            </a>   

                            </div>
                            
                        </div>
                    </div>
                </div>

            </div>

            <div class="uta-car-areas__pagination mt-5" v-if="carAreas.c && (carAreas.c.isBefore || carAreas.c.isAfter)">
                <div class="row">
                    <div class="col-12 col-xs-6 col-sm-6 col-md-4 col-xl-3 col-xxl-2 ps-0">
                        <div class="uta-pagination">
                            <button 
                                :disabled="loader || !carAreas.c.isBefore"
                                class="uta-btn uta-btn--sm uta-pagination__prev" 
                                @click.prevent="changePage(carAreas.c.start, 'before')"
                            >
                                <span class="material-icons">
                                    navigate_before
                                </span>
                            </button>
                            <div class="uta-pagination__current">
                                {{ pageNumber }}
                            </div>
                            <button 
                                :disabled="loader || !carAreas.c.isAfter"
                                class="uta-btn uta-btn--sm uta-pagination__next" 
                                @click.prevent="changePage(carAreas.c.stop, 'after')"
                            >
                                <span class="material-icons">
                                    navigate_next
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <transition name="bounce">
            <Modal 
                v-if="isShowConfigureModal"
                :header="getTranslate.CAR_AREAS_CTRL_TITLE"
                size="md"
                @closeModal="closeConfigureModal"
            >
                <template v-slot:body>
                    <UTACarAreasForm 
                        @cancel="closeConfigureModal"
                        @submitSuccess="updateCarAreasList" 
                    />   
                </template>
            </Modal>
        </transition>

        <transition name="bounce">
            <Modal 
                v-if="isShowDeleteModal"
                :header="getTranslate.CAR_AREAS_DELETE_TITLE"
                size="md"
                @closeModal="closeDeleteModal"
            >
                <template v-slot:body>
                    <div class="adt-car-areas__remove">                        
                        <div class="row">
                            <div v-if="carAreasDeleteMsg?.success == false" class="col-12">
                                <div class="mb-4 uta-alert uta-alert--danger">
                                    <span class="material-icons uta-alert__icon">error_outline</span>
                                    <div class="uta-alert__text">
                                        {{ getTranslate[carAreasDeleteMsg.text] }}
                                    </div>
                                    <button type="button" class="uta-alert__close" @click="carAreasStore.setCarAreasDeleteMsg({})">
                                        <span class="material-icons">
                                            close
                                        </span>
                                    </button>
                                </div>    
                            </div>
                            <div class="col-12">
                                {{ getTranslate.CAR_AREAS_DELETE_TEXT }}
                            </div>
                        </div>
                        <div class="row">
                            <div class="mt-3 uta-btns">
                                <button 
                                    type="button" 
                                    class="uta-btn uta-btn" 
                                    @click="closeDeleteModal"
                                >{{ getTranslate.BTN_CANCEL }}</button>
                                <button 
                                    type="button" 
                                    class="uta-btn uta-btn--main" 
                                    @click.prevent="removeCarArea"
                                >{{ getTranslate.BTN_DELETE }}</button>
                            </div>
                        </div>
                    </div>
                </template>
            </Modal>
        </transition>

    </div>
</template>